import { useCards } from "@/pages/Dashboard";
import { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Calendar, ChevronDown, Eye } from "lucide-react";

const stageBadge = {
  "POC": "bg-blue-100 text-blue-700",
  "Pilot": "bg-amber-100 text-amber-700",
  "Production": "bg-emerald-100 text-emerald-700",
  "Support": "bg-purple-100 text-purple-700"
};

const statusBadge = {
  "In Progress": "bg-blue-100 text-blue-700",
  "Completed": "bg-emerald-100 text-emerald-700",
  "Blocked": "bg-red-100 text-red-700"
};

function getCardTimeline(card) {
  const ls = card.layer_statuses || {};
  let earliest = null, latest = null;
  Object.values(ls).forEach(l => {
    if (l.start_date && (!earliest || l.start_date < earliest)) earliest = l.start_date;
    if (l.end_date && (!latest || l.end_date > latest)) latest = l.end_date;
  });
  return { earliest, latest };
}

function CardRow({ card }) {
  const { openCardWithComponent } = useCards();
  const timeline = getCardTimeline(card);
  const isOverdue = timeline.latest && new Date(timeline.latest) < new Date() && card.overall_status !== "Completed";
  const hwConfigs = card.hardware_configs || [];

  return (
    <TableRow
      className={`${card.blocker_count > 0 ? 'bg-red-50' : ''} ${isOverdue ? 'bg-amber-50' : ''}`}
      data-testid={`deadline-row-${card.id}`}
    >
      <TableCell className="text-sm">{card.vertical}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <span className="font-mono text-sm font-bold">{card.sku}</span>
          {hwConfigs.length > 0 && <span className="text-[9px] px-1 rounded bg-purple-100 text-purple-700">Multi</span>}
        </div>
      </TableCell>
      <TableCell className="max-w-[200px]">
        <p className="text-sm truncate">{card.card_title}</p>
        <p className="text-xs text-slate-500">{card.card_type}</p>
      </TableCell>
      <TableCell className="text-sm text-slate-600">{card.project_client}</TableCell>
      <TableCell>
        <span className={`text-xs px-1.5 py-0.5 rounded ${stageBadge[card.lifecycle_stage]}`}>{card.lifecycle_stage}</span>
      </TableCell>
      <TableCell>
        <span className={`text-xs px-1.5 py-0.5 rounded ${statusBadge[card.overall_status]}`}>{card.overall_status}</span>
      </TableCell>
      <TableCell>
        {card.blocker_count > 0 ? (
          <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-1 w-fit">
            <AlertTriangle className="w-3 h-3" /> {card.blocker_count}
          </span>
        ) : <span className="text-xs text-slate-400">0</span>}
      </TableCell>
      <TableCell>
        {timeline.latest ? (
          <span className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
            <Calendar className="w-3 h-3" />
            {timeline.earliest || '?'} - {timeline.latest}
            {isOverdue && " !"}
          </span>
        ) : <span className="text-xs text-slate-400">--</span>}
      </TableCell>
      <TableCell>
        <button onClick={() => openCardWithComponent(card)}
          className="p-1 hover:bg-slate-100 rounded" data-testid={`view-${card.id}`}
        >
          <Eye className="w-4 h-4 text-slate-600" />
        </button>
      </TableCell>
    </TableRow>
  );
}

function ArchiveTable({ cards }) {
  const [expanded, setExpanded] = useState(false);
  if (cards.length === 0) return null;

  return (
    <div className="mt-6 pt-4 border-t border-slate-200">
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-3"
        data-testid="archive-toggle-deadlines"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        Archived ({cards.length})
      </button>
      {expanded && (
        <div className="border rounded overflow-hidden opacity-60">
          <Table>
            <TableBody>
              {cards.map(card => <CardRow key={card.id} card={card} />)}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function DeadlineView({ activeCards, archivedCards }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Apply local date filter using layer timelines
  const filteredActive = useMemo(() => {
    let result = [...activeCards];
    if (dateFrom) result = result.filter(c => {
      const t = getCardTimeline(c);
      return t.latest && t.latest >= dateFrom;
    });
    if (dateTo) result = result.filter(c => {
      const t = getCardTimeline(c);
      return t.earliest && t.earliest <= dateTo;
    });
    return result;
  }, [activeCards, dateFrom, dateTo]);

  const filteredArchived = useMemo(() => {
    let result = [...archivedCards];
    if (dateFrom) result = result.filter(c => {
      const t = getCardTimeline(c);
      return t.latest && t.latest >= dateFrom;
    });
    if (dateTo) result = result.filter(c => {
      const t = getCardTimeline(c);
      return t.earliest && t.earliest <= dateTo;
    });
    return result;
  }, [archivedCards, dateFrom, dateTo]);

  const overdueCount = filteredActive.filter(c => {
    const t = getCardTimeline(c);
    return t.latest && new Date(t.latest) < new Date();
  }).length;
  const blockedCount = filteredActive.filter(c => c.blocker_count > 0).length;

  return (
    <div data-testid="deadline-view">
      {/* Date range filter - Deadlines page only */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 rounded border border-slate-200 flex-wrap" data-testid="deadline-date-filters">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-600">From:</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="w-[140px] h-8 text-sm" data-testid="deadline-filter-from" />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-600">To:</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="w-[140px] h-8 text-sm" data-testid="deadline-filter-to" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }}
            className="text-xs text-blue-600 hover:underline" data-testid="deadline-clear-dates"
          >Clear Dates</button>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 ml-auto text-sm">
          {blockedCount > 0 && (
            <span className="text-red-600 font-medium flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {blockedCount} blocked
            </span>
          )}
          {overdueCount > 0 && <span className="text-amber-600 font-medium">{overdueCount} overdue</span>}
          <span className="text-slate-500">{filteredActive.length} cards</span>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[100px]">Vertical</TableHead>
              <TableHead className="w-[100px]">SKU</TableHead>
              <TableHead>Card</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]">Blockers</TableHead>
              <TableHead className="w-[120px]">Deadline</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredActive.map(card => <CardRow key={card.id} card={card} />)}
          </TableBody>
        </Table>
      </div>

      {filteredActive.length === 0 && (
        <div className="text-center py-8 text-slate-500">No active cards match your filters</div>
      )}

      <ArchiveTable cards={filteredArchived} />
    </div>
  );
}
